/**
 * @React
 */
import React, { useEffect, useState } from "react";
/**
 * @DateFormater
 */
import dateFormat from "dateformat";
/**
 * @Mui
 */
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardContent,
  IconButton,
  Divider,
} from "@mui/material";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DownloadDoneOutlinedIcon from "@mui/icons-material/DownloadDoneOutlined";
/**
 * @Context
 */
import { useGeneralInfoContext } from "../../../context";
/**
 * @Components
 */
import Switch from "../../../components/CustomComponents/SwitchComponent";
import Pagination from "../../../components/common/Pagination";
import TableSkeleton from "../../../components/TableSkeleton";
import CustomTooltip from "../../../components/CustomComponents/CustomTooltip";
import CustomInputFields from "../../../components/CustomComponents/CustomInputFields";
/**
 * @Types
 */
import { ApplicationPreview, Type, Filter, monitoringStatus, IMonitoringApprovalHistory, IMonitoringAgreementCgsModel } from "../../../types";
/**
 * @Utilities
 */
import openInNewTab from "../../../utilities/newTab";
import { useFetchData } from "../../../hooks";
import { generateUrl } from "../../../utilities/generateRequestUrl";
import config from "../../../utilities/generateRequestUrl/config";
import { ApiCgsListResponse, IMonitoringCgsFields } from "../../../types/monitoringCgsFields";
import axiosInstance from "../../../utilities/axios";
import { isExpire, isMoreThanTwoWeeks } from "../../../utilities/dates";
import DialogWindow from "../../../components/CustomComponents/DialogWindow";
import { set } from "react-hook-form";
import { useSelectApplications } from "../../../hooks/useSelectAll";

const getStatusColor = (tab: Type, application: any): { backgroundColor: string; color: boolean } => {
  let backgroundColor = "inherit";
  let color = false;
  const isExpired = isExpire(new Date(), application.monitoring_deadline);

  if (
    ["UNDER_REVIEW", "APPROVED", "REJECTED", "REMONITORING_PENDING_AGENCY", "UNDER_REVIEW", "REMONITORING", "REMOVAL"].includes(
      application.monitoring_approval_status
    ) ||
    (tab === Type.agreement && application.is_monitorable)
  ) {
    color = true; // white
  }
  if ((tab === Type.agreement && application.is_monitorable && !isExpired) || ["APPROVED", "UNDER_REVIEW"].includes(application.monitoring_approval_status)) {
    backgroundColor = "#357a38"; // green
  }

  if (["REJECTED", "REMOVAL"].includes(application.monitoring_approval_status) || (tab === Type.agreement && isExpired)) {
    backgroundColor = "#F57C00"; // orange
  }

  if (tab === Type.monitoring && application.monitoring_approval_status === "REMONITORING") {
    backgroundColor = "#1565c0"; // blue
  }

  if (["REMONITORING_PENDING_AGENCY"].includes(application.monitoring_approval_status)) {
    backgroundColor = "#c62828"; // red
  }
  return { backgroundColor, color };
};

const tabStatusMap: Record<string, (keyof typeof monitoringStatus)[]> = {
  monitoring: ["REMONITORING", "REMONITORING_PENDING_AGENCY", "UNDER_REVIEW", "REJECTED"], // show only some
  approved: ["APPROVED", "REMOVAL"], // show re-monitoring related
};

interface MonitoringDashboardProps {
  tab: Type;
  isChecked: boolean;
  setIsChecked: React.Dispatch<React.SetStateAction<boolean>>;
}

const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({ tab, isChecked, setIsChecked }) => {
  const [filter, setFilter] = useState<Filter>({
    company_name: "",
    company_identification_code: "",
    page: 1,
    amountOnPage: 15,
    type: Type.monitoring,
    municipality: "",
    component: "",
    ...(Type.agreement === tab && { is_monitorable: true }),
    ...(Type.agreement !== tab && { monitoring_approval_status: "" }),
    ...(Type.monitoring === tab && { is_deadline_approve: true }),
    ...(Type.approved === tab && { is_paid: true }),
  });

  const setFilterState = (newFilter: Partial<Filter>) => {
    setFilter((prev) => {
      const shouldResetPage = Object.keys(newFilter).some((key) => key !== "page" && newFilter[key as keyof Filter] !== prev[key as keyof Filter]);

      return {
        ...prev,
        ...newFilter,
        page: shouldResetPage ? 1 : newFilter.page ?? prev.page,
      };
    });
  };

  type Column = {
    id: keyof ApplicationPreview;
    label: string;
  };

  const { data, load, error, fetchData } = useFetchData<ApiCgsListResponse | null>(generateUrl(config.client, "getAgreementCgs", null, filter as any));
  const {
    selectedIds,
    allSelected,
    someSelected,
    singleSelected,
    isModalOpen,
    setIsModalOpen,
    toggleSelectAll,
    toggleItem,
    isBulkAction,
    setIsBulkAction,
  } = useSelectApplications(
    (data?.results && data?.results.length > 0
      ? data.results
        .filter((item) => !!item._id)
        .map((item) => {
          return ({
            _id: item._id as string,
            is_paid: Boolean((item as any).is_paid),
          })
        })
      : []) as { _id: string; is_paid: boolean }[]
  );

  const toggleAgreementMonitoring = async (id: string, is_monitorable: boolean) => {
    try {
      await axiosInstance.put(`/update-agreement-cgs-is-monitorable/${id}/${is_monitorable}`);
      if (fetchData) {
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };
  const toggleCgsMonitoringPaidStatus = async (id: string, is_paid: boolean) => {
    try {
      await axiosInstance.put(`/update-cgs-monitoring-application-payment-status/${id}/${is_paid}`);
      if (fetchData) {
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };
  const toggleCgsMonitoringManyApplicationsPaidStatus = async (applications: { id: string; is_paid: boolean }[]) => {
    try {
      await axiosInstance.put(`/update-many-cgs-monitoring-application-payment-status`, { applications });
      if (fetchData) {
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const canShowEditOrAdd = (tab: Type, application: ApplicationPreview): boolean => {
    const role = localStorage.getItem("role");
    const name = localStorage.getItem("name");
    if (role !== "ROLE_MONITORING_CGS" || !application?.is_monitorable) return false;
    if (tab === Type.agreement && !application?.monitoring_approval) return true;
    if (application?.monitoring_approval_status === "REJECTED" && name === application?.created_by?.name) return true;

    return false;
  };

  useEffect(() => {
    setFilter((prev) => ({ ...prev, type: tab, monitoring_approval_status: "", page: 1 }));
  }, [tab]);

  let { options: GeneralInfo, generatedValues: GeneralInforValues } = useGeneralInfoContext();

  const columns: Column[] = [
    { id: "id", label: "" },
    ...(tab === Type.approved ? ([{ id: "is_paid", label: "ჩატარებული მონიტორინგის ანაზღაურების სტატუსი" }] as Column[]) : []),
    { id: "loan_id", label: "სესხის ID" },
    { id: "company_name", label: "ბენეფიციარის დასახელება" },
    { id: "company_identification_code", label: "ბენეფიციარის ს/კ" },
    { id: "approved_amount", label: "დამტკიცებული თანხა - ₾" },
    { id: "contract_signed_date", label: "დადასტურების თარიღი" },
    { id: "actual_municipality", label: "საქმიანობის განხორციელების ფაქტობრივი მისამართი - მუნიციპალიტეტი" },
    { id: "program_component", label: "პროგრამის კომპონენტი" },
    ...(tab !== Type.agreement ? ([{ id: "created_by", label: "მონიტორინგის განმხორციელებელი პირი" }] as Column[]) : []),
    ...(tab !== Type.agreement ? ([{ id: "monitoring_approval_status", label: "სტატუსი" }] as Column[]) : []),
    ...(tab === Type.monitoring ? ([{ id: "monitoring_new_deadline", label: "მონიტორინგის განხორციელების ვადა" }] as Column[]) : []),
    ...(tab === Type.agreement ? ([{ id: "monitoring_deadline", label: "მონიტორინგის განხორციელების ვადა" }] as Column[]) : []),
    ...(tab === Type.approved ? ([{ id: "is_monitoring_deadline", label: "დამონიტორინგებული თუ არის ვადებში" }] as Column[]) : []),
  ];
  //დამონიტორინგებული თუ არის ვადებში (ორ კვირაში)
  function redirectToDetailsOrCreate(url: string, callback: (a: string) => void) {
    if (callback) callback(url);
  }
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          padding: 2,
          "& > *": {
            flex: "0 1 calc(33.333% - 8px)", // 3 per row, equal width
            minWidth: "250px",               // optional: prevent items from being too small
          },
        }}
      >
        <TextField
          fullWidth
          sx={{
            fontSize: "12px",
            label: {
              fontSize: "14px",
            },
          }}
          variant="outlined"
          label="ბენეფიციარის დასახელება"
          value={filter.company_name}
          onChange={(e) => setFilterState({ company_name: e.target.value })}
        />
        <TextField
          fullWidth
          sx={{
            fontSize: "12px",
            label: {
              fontSize: "14px",
            },
          }}
          variant="outlined"
          label="ბენეფიციარის ს/კ"
          value={filter.company_identification_code}
          onChange={(e) => setFilterState({ company_identification_code: e.target.value })}
        />
        <FormControl variant="outlined" fullWidth>
          <InputLabel
            id="municipality"
            sx={{
              fontSize: "14px",
              // width: "100%",
            }}
          >
            მუნიციპალიტეტი
          </InputLabel>
          <Select
            // sx={{ width: "300px" }}
            labelId="municipality"
            value={filter.municipality}
            onChange={(e) => setFilterState({ municipality: e.target.value })}
            label="მუნიციპალიტეტი"
          >
            <MenuItem
              value=""
              sx={{
                fontSize: "12px",
              }}
            >
              <em>ყველა</em>
            </MenuItem>
            {GeneralInfo?.municipalities &&
              Object.keys(GeneralInfo?.municipalities).map((key) => {
                return (
                  <MenuItem
                    key={key}
                    value={key}
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {GeneralInfo?.municipalities[key].label}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
        {(tab === Type.monitoring || tab === Type.approved) && (
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="status"
              sx={{
                fontSize: "14px",
              }}
            >
              სტატუსი
            </InputLabel>
            <Select
              // sx={{ width: "200px" }}
              labelId="statusId"
              value={filter?.monitoring_approval_status}
              onChange={(e) => setFilterState({ monitoring_approval_status: e.target.value as any })}
              label="სტატუსი"
            >
              <MenuItem
                value=""
                sx={{
                  fontSize: "12px",
                }}
              >
                <em>ყველა</em>
              </MenuItem>
              {tabStatusMap[tab].map((item) => {
                return (
                  <MenuItem
                    key={item}
                    value={item}
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {monitoringStatus[item as keyof typeof monitoringStatus]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        {tab === Type.agreement && (
          <CustomInputFields
            key={1}
            name={"monitorable"}
            value={filter?.is_monitorable || false}
            onChange={() => {
              setFilterState({
                is_monitorable: filter.is_monitorable ? false : true,
              });
            }}
            type={"checkbox"}
            label={"მონიშნულები"}
          />
        )}
        {tab === Type.approved && (
          <CustomInputFields
            key={1}
            name={"is_paid"}
            value={filter?.is_paid || false}
            onChange={() => {
              setFilterState({
                is_paid: filter.is_paid ? false : true,
              });
            }}
            type={"checkbox"}
            label={"გადახდილები"}
          />
        )}
        {["admin", "ROLE_AGENCY"].includes(localStorage.getItem("role") || "") && (
          <Switch isChecked={isChecked} callback={() => setIsChecked((prev) => !prev)} label={isChecked ? "სხვა პროგრამები" : "მიკრო"} />
        )}
      </Box>
      <Divider />
      <CardContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  if (column.id === "is_paid") {
                    return (
                      <TableCell key={column.id}>
                        {column.label}
                        {tab === Type.approved && ["ROLE_AGENCY"].includes(localStorage.getItem("role") || "")
                          &&
                          <DialogWindow
                            key={"SelectedAll"}
                            openDialogWindow={isBulkAction}
                            txt={{
                              dialogTextTitle: "",
                              cancelButton: "გაუქმება",
                              triggerButton: "შეცვლა",
                              dialogTextBody: "დარწმუნებული ხართ, რომ სტატუსის გადახდის შეცვლა გსურთ?",
                            }}
                            callback={() => {
                              toggleCgsMonitoringManyApplicationsPaidStatus(selectedIds.map(({ _id, is_paid }) => ({ id: _id, is_paid })));
                            }}
                            setOpen={(open) => {
                              if (!open) {
                                setIsBulkAction(false);
                              }
                            }}
                          >
                            <CustomInputFields
                              key={"10000"}
                              name={"selectedsAll"}
                              indeterminate={someSelected}
                              value={allSelected}
                              onChange={(checked) => {
                                toggleSelectAll(
                                  checked,
                                  (data?.results && data.results.length > 0 && data?.results.map((app: any) => (
                                    { _id: app?._id, is_paid: app?.is_paid }
                                  ))) || []);
                                // setIsModalOpen(true)
                              }

                              }
                              type={"checkbox"}
                              label={""}
                            />
                          </DialogWindow >}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  )
                })}
                <TableCell key={1001}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                data?.results.map((application: any, index: number) => {
                  return (
                    <TableRow
                      sx={{
                        backgroundColor: getStatusColor(tab, application).backgroundColor,
                      }}
                      key={application._id}
                      onDoubleClick={() =>
                        redirectToDetailsOrCreate(
                          `${[Type.monitoring, Type.approved].includes(tab) ? "/monitoring-details-cgs/" : "/monitoring-agreement-details-cgs/"}${application._id
                          }`,
                          openInNewTab
                        )
                      }
                    >
                      {columns.map((column: Column) => {
                        let value;
                        const rowIndex = index + 1 + (filter.page - 1) * filter.amountOnPage;

                        if (column.id === "id") {
                          value = `${rowIndex} `;
                        } else if (column.id === "actual_municipality") {
                          if (GeneralInforValues && GeneralInforValues["municipalities"]) {
                            value = application[column.id]
                              ? GeneralInforValues["municipalities"][application[column.id] as keyof (typeof GeneralInforValues)["municipalities"]]
                                ?.municipality
                              : "მუნიციპალიტეტი არ არის მითითებული";
                          }
                        } else if (application[column.id as keyof ApplicationPreview] && column.id === "created_by") {
                          value = application[column.id]?.name || "";
                        } else if (column.id === "is_monitoring_deadline") {
                          const approvesHistory = (application?.monitoring_approval_history as IMonitoringApprovalHistory[]) || [];
                          const approvedDate = approvesHistory[approvesHistory.length - 1]?.approved_at || null;
                          const monitoringDeadline = application.monitoring_deadline;
                          const monitoringNewDeadline = application.monitoring_new_deadline;
                          const deadline = monitoringNewDeadline || monitoringDeadline;
                          value =
                            !approvedDate || !deadline ? "" : isMoreThanTwoWeeks(new Date(deadline).getTime(), new Date(approvedDate).getTime()) ? "არა" : "კი";
                        } else if (["contract_signed_date", "monitoring_deadline"].includes(column.id)) {
                          value = application[column.id] ? dateFormat(application[column.id], "dd/mm/yyyy") : "";
                        } else if (["monitoring_new_deadline"].includes(column.id) && tab === Type.monitoring) {
                          value = application.monitoring_new_deadline
                            ? dateFormat(application.monitoring_new_deadline, "dd/mm/yyyy")
                            : application.monitoring_deadline
                              ? dateFormat(application.monitoring_deadline, "dd/mm/yyyy")
                              : "";
                        } else if (column.id === "monitoring_approval_status") {
                          value = application[column.id as keyof ApplicationPreview]
                            ? monitoringStatus[application[column.id as keyof ApplicationPreview] as keyof typeof monitoringStatus]
                            : "fake status";
                        } else if (column.id === "is_paid") {
                          if (tab === Type.approved && ["ROLE_AGENCY"].includes(localStorage.getItem("role") || "")) {
                            value = (
                              <DialogWindow
                                key={application._id + "paidStatus"}
                                openDialogWindow={isModalOpen}
                                txt={{
                                  dialogTextTitle: "",
                                  cancelButton: "გაუქმება",
                                  triggerButton: "შეცვლა",
                                  dialogTextBody: "დარწმუნებული ხართ, რომ გადახდის სტატუსის შეცვლა გსურთ?",
                                }}
                                callback={() => {
                                  if (singleSelected && singleSelected._id) {
                                    toggleCgsMonitoringPaidStatus(singleSelected._id, !singleSelected.is_paid);
                                  }
                                }}
                                setOpen={(open) => {
                                  if (!open) {
                                    setIsModalOpen(false);
                                  }
                                }}
                              >
                                <CustomInputFields
                                  key={application._id}
                                  name={application._id}
                                  value={application.is_paid}
                                  onChange={() => {
                                    toggleItem(application._id, application.is_paid || false);
                                    // setIsModalOpen(true);
                                  }}
                                  type={"checkbox"}
                                  label={""}
                                  style={{
                                    checkbox: {
                                      color: "#fff",           // normal (unchecked) color
                                      checkedColor: "#fff", // checked color
                                    },
                                  }}
                                />
                              </DialogWindow >
                            )
                          } else {
                            value = application[column.id as keyof ApplicationPreview]
                              ? "გადახდილია"
                              : "არ არის გადახდილი";
                          }
                        } else {
                          value = application[column.id as keyof ApplicationPreview];
                        }
                        return (
                          <TableCell
                            sx={{
                              color: getStatusColor(tab, application).color ? "#fff" : "inherit",
                              fontWeight: getStatusColor(tab, application).color ? "bold" : "normal",
                            }}
                            key={column.id}
                          >
                            {value || ""}
                          </TableCell>
                        );
                      })}
                      <TableCell key={`${application._id} - ${index}`} align="left">
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            width: "100px",
                          }}
                        >
                          {!application?.monitoring_approval && canShowEditOrAdd(tab, application) ? (
                            <CustomTooltip title={tab === Type.agreement ? "მონიტორინგი" : "რედაქტირება"} placement="bottom-end" arrow>
                              <IconButton
                                aria-label="edit"
                                size="small"
                                onClick={() =>
                                  tab === Type.monitoring
                                    ? redirectToDetailsOrCreate(`/monitoring-update-cgs-form/${application._id}`, openInNewTab)
                                    : redirectToDetailsOrCreate(`/monitoring-start-cgs-form/${application._id}`, openInNewTab)
                                }
                              >
                                {tab === Type.monitoring ? (
                                  <>
                                    {["ROLE_MONITORING_CGS"].includes(localStorage.getItem("role") || "") &&
                                      ["REJECTED"].includes(application.monitoring_approval_status) ? (
                                      <EditOutlinedIcon
                                        sx={{
                                          strokeWidth: 1,
                                          stroke: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                          fontSize: "1rem",
                                          color: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                        }}
                                      />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    {["ROLE_MONITORING_CGS"].includes(localStorage.getItem("role") || "") && application.is_monitorable ? (
                                      <AddCircleOutlinedIcon
                                        sx={{
                                          strokeWidth: 1,
                                          // stroke: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                          fontSize: "1rem",
                                          color: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                        }}
                                      />
                                    ) : null}
                                  </>
                                )}
                              </IconButton>
                            </CustomTooltip>
                          ) : null}
                          {tab === Type.agreement && ["ROLE_AGENCY", "ROLE_MONITORING_REVIEW"].includes(localStorage.getItem("role") || "") ? (
                            <CustomInputFields
                              key={application._id}
                              name={application._id}
                              value={application.is_monitorable}
                              onChange={() => {
                                toggleAgreementMonitoring(application._id, !application.is_monitorable);
                              }}
                              type={"checkbox"}
                              label={""}
                            />
                          ) : null}
                          {[Type.monitoring, Type.approved].includes(tab) && application.monitoring_approval && (
                            <CustomTooltip title="დადასტურებული" placement="bottom-end" arrow>
                              <IconButton aria-label="detail" size="small" sx={{ fontSize: "1em" }}>
                                <DownloadDoneOutlinedIcon
                                  sx={{
                                    strokeWidth: 1,
                                    // stroke: getStatusColor(tab, application).color ? "#fff" : "#00695f",
                                    color: getStatusColor(tab, application).color ? "#fff" : "#00695f",
                                    fontSize: "1.1rem",
                                  }}
                                />
                              </IconButton>
                            </CustomTooltip>
                          )}
                          <CustomTooltip
                            title={
                              tab === Type.agreement
                                ? "დასამონიტორინგებელი პროექტის დეტალები"
                                : `${tab === Type.monitoring ? "დამონიტორინგებული" : "დადასტურებული"} პროექტის დეტალები`
                            }
                            placement="bottom-end"
                            arrow
                          >
                            <IconButton
                              aria-label="detail"
                              size="small"
                              sx={{ fontSize: "1em" }}
                              onClick={() =>
                                redirectToDetailsOrCreate(
                                  `${[Type.monitoring, Type.approved].includes(tab) ? "/monitoring-details-cgs/" : "/monitoring-agreement-details-cgs/"}${application._id
                                  }`,
                                  openInNewTab
                                )
                              }
                            >
                              <InfoOutlinedIcon
                                fontSize="inherit"
                                sx={{
                                  strokeWidth: 1,
                                  stroke: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                  color: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                  fontSize: "1rem",
                                  // color: (((tab === Type.approved && application.monitoring_approval_status === "APPROVED")) || returned || (tab === Type.agreement && application.is_monitorable)) ? "#fff" : "#56B9E9"
                                }}
                              />
                            </IconButton>
                          </CustomTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {load && <TableSkeleton amount={5} columns={columns} />}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={data?.pages || 1}
          page={filter.page || 1}
          onChange={(event, value) => setFilter({ ...filter, page: value })}
          rowsPerPage={filter.amountOnPage || 15}
          onRowsPerPageChange={(event) => setFilter({ ...filter, amountOnPage: event.target.value as number, page: 1 })}
        />
      </CardContent >
    </>
  );
};

export default MonitoringDashboard;

// function redirectToDetailsOrCreate(application: ApplicationPreview) {
//   const { _id: id, company_identification_code, contract_number } = application;

//   if (tab === Type.monitoring) {
//     window.open(`/monitoring-details/${id}`);
//   } else {
//     window.open(`/monitoring-form`);
//   }
// }
